import React, { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import Button from '../Button/Button';
import Modal, { ModalBody, ModalFooter } from '../Modal';
import { Input, Label } from '../Form';
import useNotify from '../../hooks/useNotify';
import { ApolloError } from '@apollo/client/errors';
import { ucFirst } from '@ecosconnect/core/src/helpers/string';
import UserPicker from '../User/UserPicker';
import { format, parseISO } from 'date-fns';
import { useHistory } from 'react-router-dom';
import useMode from '../../hooks/useMode';
import FormRow from '../Form/FormRow';
import Form from '../Form/Form';
import FormColumn from '../Form/FormColumn';
import CustomerPicker from '../Customer/CustomerPicker';
import { gql } from '@/modes/waterSupplier/gql';

export const rwInspectionFields = `
  jobId
  waterSiteId
  contactProfileId
  supervisorProfileId
  job {
    id
    addressId
  }
`;

// export type RecycledWaterInspectionInput = {
//   job: JobInput;
//   waterSiteId: string;
//   contactProfileId?: string;
//   supervisorProfileId?: string;
// };

export type RecycledWaterInspectionInput = ReturnType<
  typeof gql.scalar<'RecycledWaterInspectionInput'>
>;

export type JobInput = {
  addressId: string;
  outboundAssignment?: OutboundAssignmentInput;
  scheduled?: number;
};

export type OutboundAssignmentInput = {
  vendorProfileId?: string;
  due?: number;
};

type RecycledWaterInspectionEditModalProps = {
  onClose: () => void;
} & (
  | {
      jobId: string;
      rwInspection?: RecycledWaterInspectionInput;
      action: 'edit';
    }
  | {
      jobId?: undefined;
      action: 'add';
      rwInspection: RecycledWaterInspectionInput;
    }
);

const CREATE_RW_INSPECTION = gql(`
  mutation CreateRwInspection($input: RecycledWaterInspectionInput!) {
    createRecycledWaterInspection(input: $input) {
      ${rwInspectionFields}
    }
  }
`);

const UPDATE_RW_INSPECTION = gql(`
  mutation UpdateRwInspection($jobId: ID!, $input: RecycledWaterInspectionInput!) {
    updateRecycledWaterInspection(jobId: $jobId, input: $input) {
      ${rwInspectionFields}
    }
  }
`);

const WaterSiteEditModal: React.FC<RecycledWaterInspectionEditModalProps> = ({
  onClose,
  rwInspection,
  jobId,
  action,
}) => {
  const [inspectionInput, setInspectionInput] =
    useState<RecycledWaterInspectionInput>({
      waterSiteId: rwInspection?.waterSiteId ?? '',
      job: {
        ...rwInspection?.job,
        addressId: rwInspection?.job?.addressId ?? '',
        outboundAssignment: {
          ...rwInspection?.job?.outboundAssignment,
          due: rwInspection?.job?.outboundAssignment?.due,
          vendorProfileId:
            rwInspection?.job?.outboundAssignment?.vendorProfileId ?? '',
        },
      },
      contactProfileId: rwInspection?.contactProfileId,
      supervisorProfileId: rwInspection?.supervisorProfileId,
    });

  const { showError, showSuccess } = useNotify();
  const history = useHistory();
  const mode = useMode();

  const handleJobChange = (e: any) => {
    const { name, value } = e.target;
    updateJobField(name, value);
  };

  const updateJobField = <F extends keyof JobInput>(
    name: F,
    value?: JobInput[F]
  ) => {
    console.log('updateJobField', name, value);
    if (name === 'scheduled') {
      if (value) {
        if (typeof value === 'string') {
          value = parseISO(value).valueOf() as any;
        }
      } else {
        value = undefined;
      }
    }
    setInspectionInput((prevState) => ({
      ...prevState,
      job: {
        ...prevState.job,
        [name]: value,
      },
    }));
  };

  const onCompleted = (data: any) => {
    showSuccess('Inspection saved');
    const inspection =
      data.createRecycledWaterInspection ?? data.updateRecycledWaterInspection;
    if (inspection && inspection.jobId) {
      history.push(`/${mode}/jobs/${inspection.jobId}`);
    }
    onClose();
  };

  const onError = (error: ApolloError) => {
    showError('Error saving inspection', error);
  };

  const [
    saveInspection,
    { data: saveData, loading: saveLoading, error: saveError },
  ] =
    action === 'edit'
      ? useMutation(UPDATE_RW_INSPECTION, {
          onCompleted,
          onError,
        })
      : useMutation(CREATE_RW_INSPECTION, {
          onCompleted,
          onError,
        });

  const save = useCallback(async () => {
    // if (!inspectionInput.name) {
    //   useError('Please enter a name')
    //   return
    // }
    // if (!inspectionInput.type) {
    //   useError('Please select a service type')
    //   return
    // }
    // if (!inspectionInput.addressId) {
    //   useError('Please enter an address')
    //   return
    // }
    const input: RecycledWaterInspectionInput = {
      ...inspectionInput,
    };
    await saveInspection({
      variables: { jobId: jobId!, input },
    });
  }, [jobId, inspectionInput]);

  return (
    <Modal onClose={onClose} title={`${ucFirst(action)} Inspection`}>
      <ModalBody>
        <Form>
          <FormRow>
            <FormColumn>
              <Label htmlFor="assignedToProfileId">Assigned to</Label>
              <UserPicker
                autoFocus
                userId={inspectionInput.job.outboundAssignment?.vendorProfileId}
                onChange={(user) => {
                  setInspectionInput((prevState) => ({
                    ...prevState,
                    job: {
                      ...prevState.job,
                      outboundAssignment: {
                        ...prevState.job.outboundAssignment,
                        vendorProfileId: user?.profileId ?? '',
                      },
                    },
                  }));
                }}
              />
            </FormColumn>
            <FormColumn>
              <Label htmlFor="scheduled">Scheduled for</Label>
              <Input
                type="datetime-local"
                name="scheduled"
                value={
                  inspectionInput.job.scheduled
                    ? format(
                        inspectionInput.job.scheduled,
                        'yyyy-MM-dd HH:mm:ss'
                      )
                    : ''
                }
                onChange={handleJobChange}
              />
            </FormColumn>
          </FormRow>
          <FormRow>
            {/*<FormColumn>*/}
            {/*  <Label>Owner</Label>*/}
            {/*  <CustomerPicker*/}
            {/*    customerProfileId={inspectionInput.ownerProfileId}*/}
            {/*    serviceAddressId={inspectionInput.addressId}*/}
            {/*    serviceAddressWhere={*/}
            {/*      inspectionInput.addressId*/}
            {/*        ? {*/}
            {/*            active: true,*/}
            {/*            addressId: inspectionInput.addressId,*/}
            {/*          }*/}
            {/*        : undefined*/}
            {/*    }*/}
            {/*    isClearable*/}
            {/*    onChange={(customer) =>*/}
            {/*      setInspectionInput((current) => {*/}
            {/*        return {*/}
            {/*          ...current,*/}
            {/*          ownerProfileId: customer?.profileId,*/}
            {/*        };*/}
            {/*      })*/}
            {/*    }*/}
            {/*  />*/}
            {/*</FormColumn>*/}
            <FormColumn>
              <Label>Site Contact</Label>
              <CustomerPicker
                customerProfileId={inspectionInput.contactProfileId}
                serviceAddressId={inspectionInput.job?.addressId}
                serviceAddressWhere={
                  inspectionInput.job.addressId
                    ? {
                        active: true,
                        addressId: inspectionInput.job?.addressId,
                      }
                    : undefined
                }
                isClearable
                onChange={(customer) =>
                  setInspectionInput((current) => {
                    return {
                      ...current,
                      contactProfileId: customer?.profileId,
                    };
                  })
                }
              />
            </FormColumn>
          </FormRow>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button icon="save" onClick={save}>
          Save
        </Button>
        <Button variant="outline" icon="slash-circle" onClick={onClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default WaterSiteEditModal;
